// Drawings for a FeatureCard's cover, behind its disc and icon: a waterway, contour lines, a network, or a plant.
// An app picks one from its own content; the kit names no hub. Each fills the cover edge to edge on the card's
// 260 × 120 frame, in the tone's ink. See Design > UI, "Dashboard emphasis".

import type { JSX } from "preact";

/** The cover drawings the kit offers. */
export type CoverArt = "waterway" | "contours" | "network" | "plant";

const W = 260, H = 120;
type Pt = readonly [number, number];
const n = (v: number) => Math.round(v * 10) / 10;
const line = (pts: ReadonlyArray<Pt>) => `M${pts.map(([x, y]) => `${n(x)} ${n(y)}`).join(" L")}`;
function wave(y: number, amp: number, len: number, phase = 0, x0 = -10, x1 = W + 10): Pt[] {
  const pts: Pt[] = [];
  for (let x = x0; x <= x1; x += 4) pts.push([x, y + amp * Math.sin((x / len) * 2 * Math.PI + phase)]);
  return pts;
}

const stroke = (d: string, o = 0.22, w = 1.3, dash?: string) =>
  <path d={d} fill="none" stroke="currentColor" stroke-width={w} stroke-linecap="round" stroke-linejoin="round" opacity={o} vector-effect="non-scaling-stroke" stroke-dasharray={dash} />;
const fill = (d: string, o = 0.07) => <path d={d} fill="currentColor" opacity={o} />;
const dot = (x: number, y: number, r: number, o = 0.22) => <circle cx={n(x)} cy={n(y)} r={r} fill="currentColor" opacity={o} />;
const ring = (x: number, y: number, r: number, o = 0.24, dash?: string) =>
  <circle cx={n(x)} cy={n(y)} r={r} fill="none" stroke="currentColor" stroke-width={1.2} opacity={o} vector-effect="non-scaling-stroke" stroke-dasharray={dash} />;

/** A river running into a bay, with sampling stations and one event circled. */
function waterway() {
  const bay = "M168 132 Q182 102 214 96 Q240 92 272 96";
  const stations: Pt[] = [[26, 20], [62, 50], [232, 104]];
  return <g>
    {fill(`${bay} L272 132 Z`, 0.09)}{stroke(bay, 0.28)}
    {stroke("M-10 14 C40 14 52 58 96 64 S168 70 206 100", 0.34, 1.4)}
    {stroke("M-10 26 C34 26 46 70 92 76 S160 80 194 108", 0.34, 1.4)}
    {stroke("M-10 20 C38 20 50 64 94 70 S164 75 200 104", 0.12, 1, "2 5")}
    {stroke(line(wave(112, 1.2, 30, 0, 180, 270)), 0.12, 1, "3 4")}
    {stations.map(([x, y]) => <g key={`${x}`}>{ring(x, y, 3.6, 0.3)}{dot(x, y, 1.2, 0.34)}</g>)}
    {ring(204, 102, 3.6, 0.32)}{dot(204, 102, 1.2, 0.36)}{ring(204, 102, 8, 0.3, "2 3")}
  </g>;
}

/** Elevation contours, the way a topographic map draws hills. */
function contours() {
  const hill = (cx: number, cy: number, base: number, count: number, step: number, o: number) => Array.from({ length: count }, (_, k) => {
    const r = base + k * step;
    const pts = Array.from({ length: 81 }, (_unused, i): Pt => {
      const t = (i / 80) * 2 * Math.PI;
      const rr = r * (1 + 0.1 * Math.sin(3 * t + k * 0.6) + 0.05 * Math.sin(5 * t + 1.3));
      return [cx + rr * Math.cos(t) * 1.35, cy + rr * Math.sin(t)];
    });
    return <g key={`${cx}-${k}`}>{stroke(`${line(pts)} Z`, o * (1 - k * 0.08), k % 4 === 3 ? 1.6 : 1.1)}</g>;
  });
  return <g>{hill(34, 92, 5, 8, 8, 0.3)}{hill(230, 24, 4, 7, 8, 0.3)}{hill(206, 112, 3, 3, 7, 0.18)}{dot(34, 92, 1.4, 0.3)}{dot(230, 24, 1.4, 0.3)}</g>;
}

/** A holding company, the companies under it, and their offices. */
function network() {
  const root: Pt = [130, 15];
  const mids: Pt[] = [[42, 48], [94, 76], [166, 76], [218, 48]];
  const leaves: Pt[][] = [[[12, 104], [30, 110], [52, 102]], [[76, 112], [100, 108]], [[160, 108], [184, 112]], [[208, 102], [228, 110], [250, 104]]];
  const pin = ([x, y]: Pt) => <g key={`${x}-${y}`}>
    <path d={`M${x} ${y} c-3.2-4-4.8-6.4-4.8-8.6a4.8 4.8 0 0 1 9.6 0c0 2.2-1.6 4.6-4.8 8.6z`} fill="currentColor" fill-opacity={0.1} stroke="currentColor" stroke-width={1.1} stroke-opacity={0.3} vector-effect="non-scaling-stroke" />
    {dot(x, y - 8.6, 1.3, 0.3)}
  </g>;
  const box = `M${root[0] - 8} ${root[1] - 5} h16 v10 h-16 z`;
  return <g>
    {mids.map(([x, y]) => <g key={`e${x}`}>{stroke(`M${root[0]} ${root[1]} C${root[0]} ${root[1] + 26} ${x} ${y - 26} ${x} ${y}`, 0.24, 1.2)}</g>)}
    {mids.map(([x, y], i) => <g key={`m${x}`}>
      {(leaves[i] ?? []).map(([lx, ly]) => <g key={`l${lx}`}>{stroke(`M${x} ${y} L${lx} ${ly - 9}`, 0.16, 1)}{pin([lx, ly])}</g>)}
      {fill(`M${x - 5} ${y} a5 5 0 1 0 10 0 a5 5 0 1 0 -10 0`, 0.12)}{ring(x, y, 5, 0.3)}
    </g>)}
    {fill(box, 0.12)}{stroke(box, 0.32, 1.2)}
  </g>;
}

/** A plant with stacks, piping its discharge to an outfall in the water. */
function plant() {
  const water = wave(98, 1.6, 24, 0, 196, W + 10);
  const roof = "M6 92 V66 L20 54 V66 L34 54 V66 L48 54 V66 L62 54 V66 H92 V92";
  return <g>
    {stroke("M-10 92 H192 Q198 92 198 98 V132", 0.24, 1.2)}
    {fill(`${roof} Z`, 0.07)}{stroke(roof, 0.3, 1.2)}
    {stroke("M68 66 V22 H75 V66 M80 66 V30 H86 V66 M68 26 H75 M80 34 H86", 0.3, 1.2)}
    {stroke("M71.5 20 C70 12 80 10 84 4 M83 28 C82 20 92 18 96 12", 0.14, 1.2)}
    {stroke("M92 80 H202 V102 M92 85 H197 V102", 0.3, 1.2)}{stroke("M118 78 V87 M146 78 V87 M174 78 V87", 0.24, 1.1)}
    {fill(`${line(water)} L${W + 10} ${H + 10} L196 ${H + 10} Z`, 0.07)}{stroke(line(water), 0.28)}
    {stroke("M199.5 104 q0 6 4 10 M199.5 104 q-1 7 -6 11 M199.5 104 q2 5 9 7", 0.2, 1.1)}
    {stroke(line(wave(112, 1.2, 20, 1, 210, W + 10)), 0.12, 1)}
  </g>;
}

const DRAW: Record<CoverArt, () => JSX.Element> = { waterway, contours, network, plant };

/** A hub's cover drawing, filling the cover edge to edge behind the disc and icon. */
export function CoverDrawing({ art }: { art: CoverArt }) {
  return <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">{DRAW[art]()}</svg>;
}
